import React from 'react';
import type { DataSourceDefinition } from '@splunk/dashboard-types';
import { _ } from '@splunk/ui-utils/i18n';
import type { BaseButtonProps } from '../BaseButton';
import { resolvedSearch } from './resolvedSearch';
import { ActionButtonWrapper } from './ActionButtonWrapper';
import { getButtonTitle } from '../utils/getButtonTitle';
import { useItemName } from '../utils/useItemName';

const noop = () => undefined;

export interface OpenSearchButtonProps extends BaseButtonProps {
    onOpenSearchClick?: (definition: DataSourceDefinition | null) => void;
    disabled?: (...args: unknown[]) => boolean;
}

export const OpenSearchButton = ({
    itemId,
    dashboardApi,
    itemDefinition,
    dataSources = {},
    onOpenSearchClick = noop,
    screenReaderText,
    disabled,
    type,
}: OpenSearchButtonProps): JSX.Element | null => {
    const vizName = useItemName({ type, itemDefinition });

    const primary = itemDefinition?.dataSources?.primary;
    const primaryDataSource = primary ? dataSources[primary] : null;

    const buttonTitle = getButtonTitle({
        base: _('Open in Search'),
        vizName,
        screenReaderText,
    });

    if (
        itemId == null ||
        dashboardApi == null ||
        primaryDataSource == null ||
        !primary
    ) {
        return null;
    }
    // disable button if search is not resolved
    const resolved = resolvedSearch({
        dataSourceId: primary,
        dataSourceDef: primaryDataSource,
        dashboardApi,
    });
    // Default behavior is to disable if the type is not ds.search, ds.chain, or ds.savedSearch, but app developers can override with their behavior
    const disableButton =
        typeof disabled === 'function'
            ? disabled(resolved)
            : resolved === null ||
              !(
                  resolved.type === 'ds.search' ||
                  resolved.type === 'ds.chain' ||
                  resolved.type === 'ds.savedSearch'
              );

    return (
        <ActionButtonWrapper
            dataSourceId={primary}
            dataSourceDef={primaryDataSource}
            dashboardApi={dashboardApi}
            onOpenSearchClick={onOpenSearchClick}
            screenReaderText={buttonTitle}
            disabled={disableButton}
        />
    );
};
